// src/features/equipment/components/maintenance/useMaintenanceActions.jsx
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Link, Typography } from '@mui/material';
import { AttachFile as AttachFileIcon } from '@mui/icons-material';

import MaintenanceFormDialog from './MaintenanceFormDialog';
import SelectFileDialog from '../../../files/components/SelectFileDialog/SelectFileDialog';
import { useEquipmentMaintenance } from '../../hooks/useEquipmentMaintenance';
import { useEquipmentFolder, equipmentFolderName } from '../../hooks/useEquipmentFolder';
import { useFiles } from '../../../files/hooks/useFiles';
import { FileDropZone, useFilePicker, useUploadQueue, UploadProgressList } from '../../../files/upload';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { usePermissions } from '../../../auth/hooks/usePermissions';

/**
 * All repair actions for one piece of equipment, as dialogs.
 * `onChanged(caseData)` runs after any successful action with the updated case.
 * `openCases` are the device's reported / in-progress cases; the report dialog
 * lists them so the same fault isn't reported twice.
 * Render `dialogs` once; call the `open*` functions from buttons.
 */
const NO_CASES = [];

export function useMaintenanceActions({ equipment, openCases = NO_CASES, onChanged, onOpenCase }) {
  const { t } = useTranslation(['equipment', 'common', 'files']);
  const { showSuccess, showError, showWarning } = useSnackbar();
  const permissions = usePermissions();
  const api = useEquipmentMaintenance();
  const findEquipmentFolder = useEquipmentFolder();
  const { uploadFile, deleteFile } = useFiles();

  // { kind, case } of the open dialog, or null
  const [active, setActive] = useState(null);
  const [reportFiles, setReportFiles] = useState([]);
  const [filePicker, setFilePicker] = useState(null); // { purpose, folderId, breadcrumbs }

  const canMaintain = permissions.hasScope('equipment:maintain');
  const canReport = permissions.hasScope('equipment:read');
  const equipmentId = equipment?.equipment_id;

  // A report's files are uploaded as soon as they're added, so the files of a
  // report that is never sent are deleted again instead of piling up in Filer
  const reportOpen = useRef(false);
  const unsentReportFiles = useRef([]);
  useEffect(() => { unsentReportFiles.current = reportFiles; }, [reportFiles]);

  const close = useCallback(() => {
    if (reportOpen.current) {
      reportOpen.current = false;
      unsentReportFiles.current.forEach(f => deleteFile(f.file_id));
    }
    setActive(null);
  }, [deleteFile]);

  const run = useCallback(async (call, successKey) => {
    const result = await call();
    if (result.success) {
      showSuccess(t(`equipment:maintenance.messages.${successKey}`));
      onChanged?.(result.data);
    } else {
      showError(result.error || t('equipment:maintenance.errors.action_failed'));
    }
    return result;
  }, [onChanged, showSuccess, showError, t]);

  // Picking files already in Filer: start in the equipment's folder if it has one
  const openFilePicker = useCallback(async (purpose) => {
    const folder = await findEquipmentFolder(equipmentFolderName(equipment));
    setFilePicker({ purpose, folderId: folder?.folderId ?? null, breadcrumbs: folder?.breadcrumbs ?? null });
  }, [findEquipmentFolder, equipment]);

  // New files (photos of a fault, service reports) go to the equipment's folder,
  // which is created on the first upload
  const uploadToEquipmentFolder = useCallback(async (file, { onUploadProgress }) => {
    const folder = await findEquipmentFolder(equipmentFolderName(equipment), { create: true });
    return uploadFile(file, { folder_id: folder?.folderId ?? null, is_system_file: false, onUploadProgress });
  }, [findEquipmentFolder, equipment, uploadFile]);

  // Fault report: uploads collect in reportFiles and are sent with the report
  const reportUploads = useUploadQueue({
    upload: uploadToEquipmentFolder,
    onUploaded: (result, file) => {
      const fileId = result.data?.data?.file_id;
      if (!fileId) return;
      // Finished after the report was closed
      if (!reportOpen.current) {
        deleteFile(fileId);
        return;
      }
      setReportFiles(prev => [...prev, { file_id: fileId, filename: file.name }]);
    },
  });
  const reportPicker = useFilePicker({ onFiles: reportUploads.enqueue });

  // Open case: uploads are attached to the case they were dropped on or picked for
  const lastAttached = useRef(null);
  const caseUploads = useUploadQueue({
    upload: uploadToEquipmentFolder,
    onUploaded: async (result, file, meta) => {
      const attached = await api.attachFile(meta.caseId, result.data.data.file_id);
      if (attached.success) lastAttached.current = attached.data;
      else showError(attached.error || t('equipment:errors.attach_file_failed'));
    },
    onFinished: () => {
      if (!lastAttached.current) return;
      showSuccess(t('equipment:messages.file_attached'));
      onChanged?.(lastAttached.current);
      lastAttached.current = null;
    },
  });
  const pickingForCase = useRef(null);
  const { enqueue: enqueueCaseUploads } = caseUploads;
  const casePicker = useFilePicker({
    onFiles: (files) => enqueueCaseUploads(files, { caseId: pickingForCase.current }),
  });
  const { open: openCasePicker } = casePicker;

  const outcomeChoices = (values) => values.map(value => ({
    value, label: t(`equipment:maintenance.outcome_choices.${value}`),
  }));

  const reportedCases = useMemo(() => openCases.filter(c => c.status === 'reported'), [openCases]);
  const activeCase = active?.case;
  const otherReports = activeCase
    ? reportedCases.filter(c => c.maintenance_id !== activeCase.maintenance_id)
    : [];
  // A fault resolved on the spot only asks about the device when it was taken out of use
  const resolvingOutOfService = active?.kind === 'resolve' && activeCase?.equipment?.status === 'out_of_service';

  const openExisting = (maintenanceId) => {
    close();
    onOpenCase?.(maintenanceId);
  };

  const existingReportsNotice = reportedCases.length > 0 && (
    <>
      {t('equipment:maintenance.dialogs.report.existing')}
      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
        {reportedCases.map(c => (
          <li key={c.maintenance_id}>
            <Link component="button" type="button" onClick={() => openExisting(c.maintenance_id)}>
              {c.title}
            </Link>
          </li>
        ))}
      </Box>
    </>
  );

  const serviceProviderField = {
    name: 'service_provider', label: t('equipment:maintenance.fields.service_provider'), type: 'contact',
  };

  const configs = {
    report: {
      title: t('equipment:maintenance.dialogs.report.title'),
      submitLabel: t('equipment:maintenance.actions.report_fault'),
      submitColor: 'warning',
      notice: existingReportsNotice,
      fields: [
        { name: 'title', label: t('equipment:maintenance.fields.fault_title'), required: true },
        { name: 'description', label: t('equipment:maintenance.fields.fault_description'), multiline: true },
        // Only asked while the device is in use
        ...(equipment?.status === 'operational' ? [{
          name: 'still_usable', type: 'radio',
          label: t('equipment:maintenance.fields.still_usable'),
          options: [
            { value: 'yes', label: t('equipment:maintenance.fields.still_usable_yes') },
            { value: 'no', label: t('equipment:maintenance.fields.still_usable_no') },
          ],
        }] : []),
      ],
      initialValues: { still_usable: 'yes' },
      onSubmit: ({ still_usable: stillUsable, ...v }) => {
        // Sending now would leave out files still uploading
        if (reportUploads.busy) {
          showWarning(t('equipment:maintenance.wait_for_uploads'));
          return Promise.resolve({ success: false });
        }
        return run(() => api.reportFault(equipmentId, {
          ...v, out_of_service: stillUsable === 'no', file_ids: reportFiles.map(f => f.file_id),
        }), 'fault_reported').then((result) => {
          // Sent: the files belong to the report now
          if (result.success) reportOpen.current = false;
          return result;
        });
      },
    },
    repair: {
      title: t('equipment:maintenance.dialogs.repair.title'),
      description: t('equipment:maintenance.dialogs.repair.description', { name: equipment?.name }),
      notice: otherReports.length > 0 && t('equipment:maintenance.dialogs.repair.others_close'),
      submitLabel: t('equipment:maintenance.actions.send_to_repair'),
      fields: [
        serviceProviderField,
        { name: 'message', label: t('equipment:maintenance.fields.note'), multiline: true },
      ],
      onSubmit: (v) => run(() => api.pullReportedCase(activeCase.maintenance_id, v), 'sent_to_repair'),
    },
    resolve: {
      title: t('equipment:maintenance.dialogs.resolve.title'),
      description: t('equipment:maintenance.dialogs.resolve.description'),
      submitLabel: t('equipment:maintenance.actions.resolve'),
      submitColor: 'success',
      fields: [
        { name: 'resolution', label: t('equipment:maintenance.fields.resolve_how'), multiline: true },
        ...(resolvingOutOfService ? [{
          name: 'outcome', label: t('equipment:maintenance.fields.usable_again'), type: 'radio', required: true,
          options: outcomeChoices(['operational', 'out_of_service']),
        }] : []),
      ],
      initialValues: { outcome: 'operational' },
      onSubmit: (v) => run(() => api.completeCase(activeCase.maintenance_id, v), 'resolved'),
    },
    complete: {
      title: t('equipment:maintenance.dialogs.complete.title'),
      notice: otherReports.length > 0
        && t('equipment:maintenance.dialogs.complete.closes_reports', { count: otherReports.length }),
      submitLabel: t('equipment:maintenance.actions.complete'),
      submitColor: 'success',
      fields: [
        { name: 'resolution', label: t('equipment:maintenance.fields.work_done'), multiline: true },
        serviceProviderField,
        { name: 'outcome', label: t('equipment:maintenance.fields.works_again'), type: 'radio', required: true,
          options: outcomeChoices(['operational', 'out_of_service', 'retired']) },
      ],
      initialValues: {
        outcome: 'operational',
        service_provider: activeCase?.service_provider ?? '',
        service_provider_contact_id: activeCase?.service_provider_contact_id ?? null,
      },
      onSubmit: (v) => run(() => api.completeCase(activeCase.maintenance_id, v), 'completed'),
    },
    record: {
      title: t('equipment:maintenance.dialogs.record.title'),
      description: t('equipment:maintenance.dialogs.record.description'),
      submitLabel: t('common:buttons.save'),
      fields: [
        { name: 'title', label: t('equipment:maintenance.fields.fault_title_past'), required: true },
        { name: 'resolution', label: t('equipment:maintenance.fields.work_done'), multiline: true },
        serviceProviderField,
        { name: 'performed_at', label: t('equipment:maintenance.fields.performed_at'), type: 'datetime',
          helperText: t('equipment:maintenance.fields.performed_at_help') },
      ],
      onSubmit: (v) => run(() => api.recordMaintenance(equipmentId, {
        ...v, performed_at: v.performed_at ? v.performed_at.toISOString() : null,
      }), 'recorded'),
    },
    cancel: activeCase?.status === 'in_progress' ? {
      title: t('equipment:maintenance.dialogs.cancel_repair.title'),
      description: t('equipment:maintenance.dialogs.cancel_repair.description'),
      submitLabel: t('equipment:maintenance.actions.cancel_repair'),
      submitColor: 'error',
      fields: [{ name: 'reason', label: t('equipment:maintenance.fields.cancel_reason'), multiline: true }],
      onSubmit: (v) => run(() => api.cancelCase(activeCase.maintenance_id, v), 'repair_cancelled'),
    } : {
      title: t('equipment:maintenance.dialogs.close_report.title'),
      description: t('equipment:maintenance.dialogs.close_report.description'),
      submitLabel: t('equipment:maintenance.actions.close_report'),
      submitColor: 'error',
      fields: [{ name: 'reason', label: t('equipment:maintenance.fields.cancel_reason'), multiline: true }],
      onSubmit: (v) => run(() => api.cancelCase(activeCase.maintenance_id, v), 'report_closed'),
    },
  };

  const config = active ? configs[active.kind] : null;

  // Photos and files for a fault report: drop them on the area or pick them
  const reportExtra = active?.kind === 'report' ? (
    <FileDropZone
      onFiles={reportUploads.enqueue}
      sx={{ border: 1, borderStyle: 'dashed', borderColor: 'divider', borderRadius: 1, p: 2 }}
    >
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Button size="small" startIcon={<AttachFileIcon />} onClick={reportPicker.open}>
          {t('equipment:maintenance.actions.attach_photo')}
        </Button>
        <Typography variant="caption" color="text.secondary">{t('equipment:maintenance.drop_hint')}</Typography>
      </Box>
      {reportPicker.input}
      {reportFiles.length > 0 && (
        <Box display="flex" flexWrap="wrap" gap={1} mt={1.5}>
          {reportFiles.map(f => (
            <Chip key={f.file_id} size="small" label={f.filename}
                  onDelete={() => {
                    setReportFiles(prev => prev.filter(x => x.file_id !== f.file_id));
                    deleteFile(f.file_id);
                  }} />
          ))}
        </Box>
      )}
      <UploadProgressList items={reportUploads.items} onDismiss={reportUploads.dismiss} sx={{ mt: 2 }} />
    </FileDropZone>
  ) : null;

  // Files already in Filer, picked for a case
  const handleFilesSelected = useCallback(async (selection) => {
    const purpose = filePicker?.purpose;
    setFilePicker(null);
    const fileIds = Array.isArray(selection) ? selection : [];
    // Attach to an existing case
    const caseId = purpose?.caseId;
    if (!caseId) return;
    let last = null;
    for (const fileId of fileIds) {
      last = await api.attachFile(caseId, fileId);
      if (!last.success) {
        showError(last.error || t('equipment:errors.attach_file_failed'));
        return;
      }
    }
    if (last) {
      showSuccess(t('equipment:messages.file_attached'));
      onChanged?.(last.data);
    }
  }, [filePicker, api, onChanged, showSuccess, showError, t]);

  const removeFile = useCallback((maintenanceCase, file) =>
    run(() => api.removeFile(maintenanceCase.maintenance_id, file.maintenance_file_id), 'file_removed'),
  [api, run]);

  const open = useCallback((kind, maintenanceCase = null) => {
    if (kind === 'report') {
      setReportFiles([]);
      reportOpen.current = true;
    }
    setActive({ kind, case: maintenanceCase });
  }, []);

  const dialogs = (
    <>
      {config && (
        <MaintenanceFormDialog
          open
          onClose={close}
          title={config.title}
          description={config.description}
          fields={config.fields}
          initialValues={config.initialValues}
          notice={config.notice}
          submitLabel={config.submitLabel}
          submitColor={config.submitColor}
          onSubmit={config.onSubmit}
          extra={reportExtra}
        />
      )}
      {casePicker.input}
      <SelectFileDialog
        open={Boolean(filePicker)}
        onClose={() => setFilePicker(null)}
        onSelectFile={handleFilesSelected}
        multiSelect
        allowUpload={true}
        excludedFileIds={filePicker?.purpose?.excluded || []}
        title={t('equipment:actions.choose_existing_file')}
        startingFolderId={filePicker?.folderId}
        initialBreadcrumbs={filePicker?.breadcrumbs}
      />
      <UploadProgressList
        variant="floating"
        items={caseUploads.items}
        onDismiss={caseUploads.dismiss}
        onClose={caseUploads.clearFinished}
      />
    </>
  );

  // Notes are written inline in the case drawer, so they skip the dialog
  const addNote = useCallback((maintenanceCase, message) =>
    run(() => api.addNote(maintenanceCase.maintenance_id, { message }), 'note_added'),
  [api, run]);

  const actions = useMemo(() => ({
    openReport: () => open('report'),
    openRecord: () => open('record'),
    openRepair: (c) => open('repair', c),
    openResolve: (c) => open('resolve', c),
    openComplete: (c) => open('complete', c),
    openCancel: (c) => open('cancel', c),
    // Upload new files to a case (picker, or files dropped on the drawer)
    pickFilesFor: (c) => {
      pickingForCase.current = c.maintenance_id;
      openCasePicker();
    },
    uploadTo: (c, files) => enqueueCaseUploads(files, { caseId: c.maintenance_id }),
    // Attach files that are already in Filer
    openAttach: (c) => openFilePicker({ caseId: c.maintenance_id, excluded: (c.files || []).map(f => f.file_id) }),
    addNote,
    removeFile,
  }), [open, openFilePicker, addNote, removeFile, openCasePicker, enqueueCaseUploads]);

  return {
    ...actions,
    dialogs,
    canMaintain,
    canReport,
  };
}
